import type { WorkoutRecord } from './db'

export const BACKUP_FORMAT = 'myofade-backup' as const
/** Pre-rename format; still accepted on import. */
const LEGACY_BACKUP_FORMAT = 'myoheat-backup'
export const BACKUP_VERSION = 1 as const

export interface MyoFadeBackup {
  format: typeof BACKUP_FORMAT
  version: typeof BACKUP_VERSION
  exportedAt: number
  workouts: WorkoutRecord[]
}

export function buildBackup(workouts: WorkoutRecord[], exportedAt = Date.now()): MyoFadeBackup {
  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt,
    workouts: workouts.map((w) => ({ ...w, sets: w.sets.map((s) => ({ ...s })) })),
  }
}

export function serializeBackup(backup: MyoFadeBackup): string {
  return `${JSON.stringify(backup, null, 2)}\n`
}

function isWorkoutRecord(value: unknown): value is WorkoutRecord {
  if (!value || typeof value !== 'object') return false
  const w = value as Record<string, unknown>
  return (
    typeof w.id === 'string' &&
    typeof w.name === 'string' &&
    typeof w.at === 'number' &&
    Number.isFinite(w.at) &&
    (w.source === 'qr' || w.source === 'manual') &&
    Array.isArray(w.muscles) &&
    Array.isArray(w.sets) &&
    typeof w.intensity === 'number'
  )
}

/** Parse and validate a backup JSON string. Throws on invalid input. */
export function parseBackup(raw: string): MyoFadeBackup {
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    throw new Error('Backup file is not valid JSON.')
  }
  if (!data || typeof data !== 'object') throw new Error('Backup file is empty.')
  const obj = data as Record<string, unknown>
  if (obj.format !== BACKUP_FORMAT && obj.format !== LEGACY_BACKUP_FORMAT) {
    throw new Error('Not a MyoFade backup file.')
  }
  if (obj.version !== BACKUP_VERSION) {
    throw new Error(`Unsupported backup version (${String(obj.version)}).`)
  }
  if (!Array.isArray(obj.workouts)) throw new Error('Backup is missing workouts.')
  const workouts: WorkoutRecord[] = []
  for (const row of obj.workouts) {
    if (!isWorkoutRecord(row)) throw new Error('Backup contains an invalid workout row.')
    workouts.push(row)
  }
  const exportedAt = typeof obj.exportedAt === 'number' && Number.isFinite(obj.exportedAt)
    ? obj.exportedAt
    : Date.now()
  return { format: BACKUP_FORMAT, version: BACKUP_VERSION, exportedAt, workouts }
}

export function backupFilename(exportedAt = Date.now()): string {
  const d = new Date(exportedAt)
  const stamp = d.toISOString().slice(0, 10)
  return `myofade-backup-${stamp}.json`
}
